import React, { useEffect } from "react";
import { Button, DropdownMenu, Input, MenuHeader } from "react-ui";
import { ToolPencilLineIcon } from "@skbkontur/icons";
import { useDebounce } from "use-debounce";
import { PopupMenuCaptionProps } from "@skbkontur/react-ui/internal/PopupMenu";

import { FocusManagementObserver } from "../../../Services/FocusManagement/FocusManagementObserver";
import { TextOverflowHint } from "../../../Internals/TextOverflowHint/TextOverflowHint";
import { isNotNullOrEmpty, isNullOrEmpty } from "../../../../../../Common/TypingUtils";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { FIAS_ENTER_EDIT_MODE, FIAS_HANDLE_CLICK, FIAS_SUBMIT_ADDRESS } from "../../../metricsAction";
import { ValueEditorsFocusManager } from "../../../../Helpers/JumpByEnterOnNextControl/ValueEditorsFocusManager";
import { Tooltip } from "../../../Internals/Tooltip/Tooltip";
import { FiasTestPageObjectType } from "../FiasTestPageObject";
import {
    AbsoluteResolvedModelPath,
    createModelPath,
    createRelativeResolvedModelPath,
} from "../../../../../../Common/ModelPath/ModelPath";

import { FiasApi } from "./FiasApi";
import { SuggestList } from "./SuggestAddressesList/SuggestList";
import { addressLevels, divisionType, FiasAddressFields, ParsedExtendedFiasField } from "./Types/FiasTypes";

interface FiasProps {
    bindingPath: AbsoluteResolvedModelPath;
    stringAddress: string;
    submitAddress: (address: FiasAddressFields) => void;
    onStringAddressChange: (currentText: string) => void;
    withSteads: boolean;
    warning?: string | boolean;
    error?: string | boolean;
    width?: string | number;
    maxItemsInList?: number;
    baseUrl?: string;
    addressLevel?: addressLevels;
    division?: divisionType;
    Tid?: string;
    focusByEnterOnNextControl: (event: React.KeyboardEvent) => void;
    dropDownPrefix?: React.ReactNode;
    onFocusRequest?: () => void | null;
    submitTextAddress?: (text: string) => void;
    apiClient: FiasApi;
    onBlur: () => void;
    openModalHandler: () => void;
    setFocus: boolean;
    extendedFields: ParsedExtendedFiasField[];
    hasOwnBindingPath: boolean;
    onlyInput?: boolean;
    setIsInputFocused: (isFocused: boolean) => void;
    setTestMethodInPageObject: (methods: FiasTestPageObjectType) => void;
}

export const FiasInput: React.FC<FiasProps> = props => {
    const [loading, setLoading] = React.useState<boolean>(false);
    const [debouncedInputAddress] = useDebounce<string>(props.stringAddress, 500);
    const [selectIndex, setSelectIndex] = React.useState<number>(-1);
    const [fetchedAddresses, setFetchedAddresses] = React.useState<FiasAddressFields[]>([]);
    const fiasInput = React.useRef<Input>(null);
    const dropdownMenuHandlerRef = React.useRef<PopupMenuCaptionProps>();
    const track = useMetrics();

    useEffect(() => {
        ValueEditorsFocusManager.forceUpdateFocusedByEnterControls();
        if (props.setFocus) {
            fiasInput.current?.focus();
        }
    }, []);

    useEffect(() => {
        props.setTestMethodInPageObject({
            openDropDown: () => {
                fiasInput.current?.focus();
            },
            closeDropDown: () => {
                fiasInput.current?.blur();
            },
            handleFocus: () => {
                fiasInput.current?.focus();
            },
            setSelectIndex: setSelectIndex,
            applySelectedItemForTestPageObject: applySelectedItemForTestPageObject,
        });
        return () => {
            props.setTestMethodInPageObject({});
        };
    }, [fetchedAddresses.length]);

    useEffect(() => {
        setLoading(true);
    }, [props.stringAddress]);

    useAsyncEffect(async () => {
        if (isNotNullOrEmpty(debouncedInputAddress)) {
            const addresses = await getSuggestAddressesByText(debouncedInputAddress);
            setFetchedAddresses(addresses);
        }
        setLoading(false);
    }, [debouncedInputAddress]);

    const applySelectedItemForTestPageObject = (index: number) => {
        setSelectIndex(index);
        if (index >= fetchedAddresses.length) {
            return false;
        }
        const itemAddress = fetchedAddresses[index];
        if (itemAddress) {
            submitAddress(itemAddress);
            return true;
        }
        return false;
    };

    const handleOpenDropdown = () => {
        props.setIsInputFocused(true);
    };

    const handleCloseDropdown = () => {
        props.onBlur();
        props.setIsInputFocused(false);
    };

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentText = event.target.value;
        props.onStringAddressChange(currentText);
    };

    const getSuggestAddressesByText = async (text: string) => {
        const addresses = await props.apiClient.fetchSuggestAddressesByText(text);
        return addresses;
    };

    const submitAddress = (address: FiasAddressFields): void => {
        props.onStringAddressChange(`${address?.suggest?.value} `);
        fiasInput.current?.focus();
        props.submitAddress(address);
        if (address.stead || address.room) {
            props.onBlur();
        }
        track(FIAS_SUBMIT_ADDRESS, {
            type: address.stead ? "stead" : "address",
            code: address.region?.code ?? null,
        });
    };

    const selectPreviousItem = () => {
        setSelectIndex(prev => Math.max(-1, prev - 1));
    };

    const selectNextItem = () => {
        setSelectIndex(prev => Math.min(prev + 1, fetchedAddresses.length - 1));
    };

    const applySelectedItem = async () => {
        const itemAddress = fetchedAddresses[selectIndex];
        if (itemAddress) {
            submitAddress(itemAddress);
        }
        closeAndComplete();
    };

    const closeAndComplete = (event?: React.KeyboardEvent<HTMLInputElement>) => {
        fiasInput.current?.blur();
        dropdownMenuHandlerRef.current?.closeMenu();

        if (event) {
            props.focusByEnterOnNextControl(event);
        }
    };

    const handleKeyPress = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case "ArrowDown":
                selectNextItem();
                event.preventDefault();
                break;
            case "ArrowUp":
                selectPreviousItem();
                event.preventDefault();
                break;
            case "Enter":
                if (!dropdownMenuHandlerRef.current?.opened || selectIndex === -1) {
                    closeAndComplete(event);
                } else {
                    await applySelectedItem();
                }
                break;
            case "Escape":
                closeAndComplete(event);
                break;
            default:
                break;
        }

        if (event.key.length > 1) {
            track(FIAS_HANDLE_CLICK, {
                key: event.key,
                textAddress: props.stringAddress,
            });
        }
    };

    const handleFocus = () => {
        track(FIAS_ENTER_EDIT_MODE, {
            withEmptyAddress: isNullOrEmpty(props.stringAddress),
        });
    };

    const handleRequestFocus = (focusPath: unknown) => {
        const isFiasChildPath =
            focusPath != undefined &&
            typeof focusPath === "string" &&
            props.extendedFields.some(it =>
                createModelPath(focusPath, true).endsWith(createRelativeResolvedModelPath(it.path))
            );
        const isFiasRootPath =
            focusPath != undefined &&
            typeof focusPath === "string" &&
            props.hasOwnBindingPath &&
            focusPath === props.bindingPath.toString();
        if (!isFiasChildPath && !isFiasRootPath) {
            return;
        }

        fiasInput.current?.focus();
        dropdownMenuHandlerRef.current?.openMenu();
        handleFocus();
    };

    const handleOpenModal = () => {
        props.openModalHandler();
    };

    const DropdownHeader = () => {
        if (loading) {
            return null;
        } else if (isNullOrEmpty(props.stringAddress)) {
            return <MenuHeader>Поиск по ФИАС</MenuHeader>;
        } else if (fetchedAddresses.length === 0) {
            return <MenuHeader>По запросу: {props.stringAddress} ничего не найдено</MenuHeader>;
        } else {
            return null;
        }
    };

    const errorOrWarning = props.error || props.warning;
    const tooltipTrigger = errorOrWarning ? "hover&focus" : "closed";
    const width = props.width ?? "100%";

    const DropdownInput = (popupMenuProps: PopupMenuCaptionProps) => {
        dropdownMenuHandlerRef.current = popupMenuProps;

        const onFocus = () => {
            popupMenuProps.openMenu();
            setTimeout(() => {
                fiasInput.current?.focus();
            }, 0);

            handleFocus();
        };

        return (
            <Input
                error={Boolean(props.error)}
                warning={Boolean(props.warning)}
                placeholder={"Начните с населенного пункта или региона"}
                onFocus={onFocus}
                maxLength={350}
                width={width}
                onChange={handleChange}
                value={props.stringAddress}
                onKeyDown={handleKeyPress}
                ref={fiasInput}
                data-tid={props.Tid}
            />
        );
    };

    return (
        <div>
            <TextOverflowHint showHint={!props.onlyInput || !errorOrWarning} text={props.stringAddress}>
                <FocusManagementObserver onRequestFocus={handleRequestFocus}>
                    <Tooltip pos={"top center"} trigger={tooltipTrigger} value={errorOrWarning} useWrapper={true}>
                        <DropdownMenu
                            caption={DropdownInput}
                            width={width}
                            menuWidth={width}
                            onOpen={handleOpenDropdown}
                            onClose={handleCloseDropdown}
                            positions={["bottom left"]}
                            footer={
                                <Button onClick={handleOpenModal} data-tid={"Fias-Handle-Edit"} use="link">
                                    <ToolPencilLineIcon /> Заполнить вручную
                                </Button>
                            }>
                            <SuggestList
                                loading={loading}
                                selectIndex={selectIndex}
                                fetchedAddresses={fetchedAddresses}
                                submitAddressHandler={submitAddress}
                                setSelectIndex={setSelectIndex}
                                inputRef={fiasInput}
                                dropDownPrefix={DropdownHeader()}
                            />
                        </DropdownMenu>
                    </Tooltip>
                </FocusManagementObserver>
            </TextOverflowHint>
        </div>
    );
};
